import React, { useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import _ from 'lodash';
import cx from 'classnames';
import { Button, Skeleton, Space } from 'antd';

import { ReactComponent as GoBackIcon } from 'helpers/icons/go-back-icon.svg';
import { ReactComponent as SelectArrowIcon } from 'helpers/icons/SelectArrow.svg';
import { PATH_ADMINISTRATION, PATH_REPORTS, TAB_CURATOR_REPORT, TAB_CURATORS_TIMESHEET } from 'helpers/constants/_common/MenuCases';
import { UserDetails } from 'api/User/types';
import buttons from 'helpers/styles/components/buttons.module.sass';

import s from '../helpers/styles.module.sass';
import editStyles from './styles.module.sass';
import CuratorViewEmployeesList from './components/CuratorViewEmployeesList';
import TrackerSidebar from '../NewTrackerSidebar';
import TrackerLogo from '../../_shared/TrackerLogo/TrackerLogo';
import { EDIT_CURATOR_TABS } from '../../../helpers/constants/_common/tabs/curator';

interface EditProfileTabSidebarProps {
  userData?: UserDetails;
  loading: boolean;
}

const CuratorViewSidebar: React.FC<EditProfileTabSidebarProps> = ({ loading, userData }) => {
  const history = useHistory();
  const location = useLocation<{ from?: string }>();
  const { userId, tab } = useParams<{ userId: string, tab: string }>();

  const [ isSearchMode, setIsSearchMode ] = useState(false);

  const handleOnClickMenu = (path: string) => {
    if (!userData) {
      return;
    }

    history.push(`${PATH_ADMINISTRATION}/${TAB_CURATORS_TIMESHEET}/${userData.userId}/${path}`);
  };

  const renderMenuItems = () => _.map(EDIT_CURATOR_TABS, (menuTab) => {
    if (userData?.usertype === menuTab.hidden) {
      return null;
    }

    return (
      <li
        key={menuTab.path}
        className={cx(s.sidebarListItem, { [s.activeSidebarList]: tab === menuTab.path })}
      >
        <span className={s.sidebarListItemLink} onClick={() => handleOnClickMenu(menuTab.path)}>
          {menuTab.name}
        </span>
      </li>
    );
  });

  const renderUserAvatar = () => (
    userData?.avatarUrl
      ? <img className={editStyles.avatar} src={userData?.avatarUrl} alt="avatar" />
      : <TrackerLogo className={editStyles.companyLogo} />
  );

  const renderUserInfo = () => {
    if (loading) {
      return (
        <Space>
          <Skeleton.Avatar active size="large" shape="circle" />
          <Skeleton.Input style={{ width: 125 }} active size="large" />
        </Space>
      );
    }

    return (
      <Button
        title={userData ? `${userData.forename} ${userData.surname}` : ''}
        className={editStyles.profilePickerWrapper}
        onClick={() => setIsSearchMode(prevState => !prevState)}
      >
        { renderUserAvatar() }

        <span className={cx(editStyles.avatarDesc, { [editStyles.avatarDescEmpty]: _.isEmpty(userData) })}>{userData ? `${userData.forename} ${userData.surname}` : 'Select a user...'}</span>
        { isSearchMode ? <SelectArrowIcon className={s.searchModeArrow} /> : <SelectArrowIcon /> }
      </Button>
    );
  };

  const renderCuratorViewActivities = () => {
    if ((tab !== EDIT_CURATOR_TABS.timesheet.path) || !userData) {
      return null;
    }

    return (
      <div className={cx(s.sidebarActivities, { [s.hidden]: isSearchMode })}>
        <TrackerSidebar
          collapsed={false}
          userData={userData}
          noProfileHeader
          curatorOnlyView
        />
      </div>
    );
  };

  const renderSidebarContent = () => (
    <>
      <ul className={cx(s.sidebarList, editStyles.sidebarList, { [editStyles.sidebarListVisible]: !isSearchMode })}>
        {renderMenuItems()}
      </ul>

      <CuratorViewEmployeesList
        visible={isSearchMode}
        setVisible={setIsSearchMode}
      />

      { renderCuratorViewActivities() }

    </>
  );

  const goBackPath = (location.state?.from === PATH_REPORTS) ? `${PATH_REPORTS}/${TAB_CURATOR_REPORT}` : `${PATH_ADMINISTRATION}/${TAB_CURATORS_TIMESHEET}`;
  const goBackTitle = (location.state?.from === PATH_REPORTS) ? 'Back to reports' : 'Back to administration';

  return (
    <div className={s.sidebarLayout}>
      <div className={s.sidebarContentWrapper}>
        <div className={s.sidebarHeader}>
          <Button
            type="primary"
            className={cx(buttons.qsButtonPrimary, s.goBackButton)}
            onClick={() => history.push(goBackPath)}
          >
            <GoBackIcon />
            { goBackTitle }
          </Button>

          <Space direction="vertical" className={editStyles.headerWrapper}>
            <span className={editStyles.avatarTitle}>
              Now viewing
            </span>

            { renderUserInfo() }
          </Space>
        </div>

        { renderSidebarContent() }

      </div>
    </div>
  );
};

export default CuratorViewSidebar;
